var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _safeAppsSdkConnector, _ethLibAdapter, _transactionManager, _networks, _ownerAccount, _saltNonce, _isConnectedToSafe, _contract, _multiSend, _proxyFactory, _masterCopyAddress, _fallbackHandlerAddress;
import SafeAppsSdkConnector from './safeAppsSdkConnector';
import CpkTransactionManager from './transactionManagers/CpkTransactionManager';
import { defaultNetworks } from './config/networks';
import cpkFactoryAbi from './abis/CpkFactoryAbi.json';
import safeAbi from './abis/SafeAbi.json';
import multiSendAbi from './abis/MultiSendAbi.json';
import { predeterminedSaltNonce, sentinelModules } from './utils/constants';
import { joinHexData, getHexDataLength } from './utils/hexData';
import { OperationType, standardizeSafeAppsTransaction } from './utils/transactions';
import { standardizeTransaction, normalizeGasLimit } from './utils/transactions';
import { checkConnectedToSafe } from './utils/checkConnectedToSafe';
class CPK {
    constructor(opts) {
        _safeAppsSdkConnector.set(this, void 0);
        _ethLibAdapter.set(this, void 0);
        _transactionManager.set(this, void 0);
        _networks.set(this, void 0);
        _ownerAccount.set(this, void 0);
        _saltNonce.set(this, predeterminedSaltNonce);
        _isConnectedToSafe.set(this, false);
        _contract.set(this, void 0);
        _multiSend.set(this, void 0);
        _proxyFactory.set(this, void 0);
        _masterCopyAddress.set(this, void 0);
        _fallbackHandlerAddress.set(this, void 0);
        __classPrivateFieldSet(this, _safeAppsSdkConnector, new SafeAppsSdkConnector());
        __classPrivateFieldSet(this, _networks, Object.assign({}, defaultNetworks));
        if (!opts) {
            return;
        }
        const { ethLibAdapter, transactionManager, ownerAccount, networks, saltNonce } = opts;
        if (!ethLibAdapter) {
            throw new Error('ethLibAdapter property missing from options');
        }
        __classPrivateFieldSet(this, _ethLibAdapter, ethLibAdapter);
        __classPrivateFieldSet(this, _transactionManager, transactionManager ? transactionManager : new CpkTransactionManager());
        __classPrivateFieldSet(this, _ownerAccount, ownerAccount);
        __classPrivateFieldSet(this, _networks, Object.assign(Object.assign({}, defaultNetworks), networks));
        if (saltNonce) {
            __classPrivateFieldSet(this, _saltNonce, saltNonce);
        }
    }
    static create(opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const cpk = new CPK(opts);
            if (opts) {
                yield cpk.init();
            }
            return cpk;
        });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!__classPrivateFieldGet(this, _ethLibAdapter)) {
                throw new Error('CPK uninitialized ethLibAdapter');
            }
            const networkId = yield __classPrivateFieldGet(this, _ethLibAdapter).getNetworkId();
            const network = __classPrivateFieldGet(this, _networks)[networkId];
            if (!network) {
                throw new Error(`unrecognized network ID ${networkId}`);
            }
            __classPrivateFieldSet(this, _masterCopyAddress, network.masterCopyAddress);
            __classPrivateFieldSet(this, _fallbackHandlerAddress, network.fallbackHandlerAddress);
            const ownerAccount = yield this.getOwnerAccount();
            __classPrivateFieldSet(this, _isConnectedToSafe, yield checkConnectedToSafe(__classPrivateFieldGet(this, _ethLibAdapter).getProvider()));
            __classPrivateFieldSet(this, _multiSend, __classPrivateFieldGet(this, _ethLibAdapter).getContract(multiSendAbi, network.multiSendAddress));
            if (this.isSafeApp() || __classPrivateFieldGet(this, _isConnectedToSafe)) {
                __classPrivateFieldSet(this, _contract, __classPrivateFieldGet(this, _ethLibAdapter).getContract(safeAbi, ownerAccount));
            }
            else {
                __classPrivateFieldSet(this, _proxyFactory, __classPrivateFieldGet(this, _ethLibAdapter).getContract(cpkFactoryAbi, network.proxyFactoryAddress));
                const salt = __classPrivateFieldGet(this, _ethLibAdapter).keccak256(__classPrivateFieldGet(this, _ethLibAdapter).abiEncode(['address', 'uint256'], [ownerAccount, __classPrivateFieldGet(this, _saltNonce)]));
                const initCode = __classPrivateFieldGet(this, _ethLibAdapter).abiEncodePacked({ type: 'bytes', value: yield __classPrivateFieldGet(this, _proxyFactory).call('proxyCreationCode', []) }, {
                    type: 'bytes',
                    value: __classPrivateFieldGet(this, _ethLibAdapter).abiEncode(['address'], [network.masterCopyAddress])
                });
                const proxyAddress = __classPrivateFieldGet(this, _ethLibAdapter).calcCreate2Address(__classPrivateFieldGet(this, _proxyFactory).address, salt, initCode);
                __classPrivateFieldSet(this, _contract, __classPrivateFieldGet(this, _ethLibAdapter).getContract(safeAbi, proxyAddress));
            }
        });
    }
    isProxyDeployed() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.address) {
                throw new Error('CPK address uninitialized');
            }
            if (!this.ethLibAdapter) {
                throw new Error('CPK ethLibAdapter uninitialized');
            }
            const codeAtAddress = yield this.ethLibAdapter.getCode(this.address);
            const isDeployed = codeAtAddress !== '0x';
            return isDeployed;
        });
    }
    isSafeApp() {
        return __classPrivateFieldGet(this, _safeAppsSdkConnector).isSafeApp();
    }
    getOwnerAccount() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isSafeApp()) {
                return (_a = __classPrivateFieldGet(this, _safeAppsSdkConnector).safeAppInfo) === null || _a === void 0 ? void 0 : _a.safeAddress;
            }
            if (__classPrivateFieldGet(this, _ownerAccount)) {
                return __classPrivateFieldGet(this, _ownerAccount);
            }
            if (!__classPrivateFieldGet(this, _ethLibAdapter)) {
                throw new Error('CPK uninitialized ethLibAdapter');
            }
            return (_b = __classPrivateFieldGet(this, _ethLibAdapter)) === null || _b === void 0 ? void 0 : _b.getAccount();
        });
    }
    get ethLibAdapter() {
        return __classPrivateFieldGet(this, _ethLibAdapter);
    }
    get networks() {
        return __classPrivateFieldGet(this, _networks);
    }
    get isConnectedToSafe() {
        return __classPrivateFieldGet(this, _isConnectedToSafe);
    }
    get contract() {
        return __classPrivateFieldGet(this, _contract);
    }
    get multiSend() {
        return __classPrivateFieldGet(this, _multiSend);
    }
    get proxyFactory() {
        return __classPrivateFieldGet(this, _proxyFactory);
    }
    get masterCopyAddress() {
        return __classPrivateFieldGet(this, _masterCopyAddress);
    }
    get fallbackHandlerAddress() {
        return __classPrivateFieldGet(this, _fallbackHandlerAddress);
    }
    get saltNonce() {
        return __classPrivateFieldGet(this, _saltNonce);
    }
    get address() {
        var _a;
        if (this.isSafeApp()) {
            return (_a = __classPrivateFieldGet(this, _safeAppsSdkConnector).safeAppInfo) === null || _a === void 0 ? void 0 : _a.safeAddress;
        }
        if (!__classPrivateFieldGet(this, _contract)) {
            return undefined;
        }
        return __classPrivateFieldGet(this, _contract).address;
    }
    setEthLibAdapter(ethLibAdapter) {
        __classPrivateFieldSet(this, _ethLibAdapter, ethLibAdapter);
    }
    setTransactionManager(transactionManager) {
        if (this.isSafeApp()) {
            throw new Error('TransactionManagers are not allowed when the app is running as a Safe App');
        }
        __classPrivateFieldSet(this, _transactionManager, transactionManager);
    }
    setNetworks(networks) {
        __classPrivateFieldSet(this, _networks, Object.assign(Object.assign({}, defaultNetworks), networks));
    }
    encodeMultiSendCallData(transactions) {
        if (!__classPrivateFieldGet(this, _ethLibAdapter)) {
            throw new Error('CPK ethLibAdapter uninitialized');
        }
        const multiSend = __classPrivateFieldGet(this, _multiSend) || __classPrivateFieldGet(this, _ethLibAdapter).getContract(multiSendAbi);
        const standardizedTxs = transactions.map(standardizeTransaction);
        const ethLibAdapter = __classPrivateFieldGet(this, _ethLibAdapter);
        return multiSend.encode('multiSend', [
            joinHexData(standardizedTxs.map((tx) => ethLibAdapter.abiEncodePacked({ type: 'uint8', value: tx.operation }, { type: 'address', value: tx.to }, { type: 'uint256', value: tx.value }, { type: 'uint256', value: getHexDataLength(tx.data) }, { type: 'bytes', value: tx.data })))
        ]);
    }
    execTransactions(transactions, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isSafeApp() && transactions.length >= 1) {
                const standardizedTxs = transactions.map(standardizeSafeAppsTransaction);
                return __classPrivateFieldGet(this, _safeAppsSdkConnector).sendTransactions(standardizedTxs);
            }
            if (!this.address) {
                throw new Error('CPK address uninitialized');
            }
            if (!__classPrivateFieldGet(this, _contract)) {
                throw new Error('CPK contract uninitialized');
            }
            if (!__classPrivateFieldGet(this, _masterCopyAddress)) {
                throw new Error('CPK masterCopyAddress uninitialized');
            }
            if (!__classPrivateFieldGet(this, _fallbackHandlerAddress)) {
                throw new Error('CPK fallbackHandlerAddress uninitialized');
            }
            if (!__classPrivateFieldGet(this, _ethLibAdapter)) {
                throw new Error('CPK ethLibAdapter uninitialized');
            }
            if (!__classPrivateFieldGet(this, _transactionManager)) {
                throw new Error('CPK transactionManager uninitialized');
            }
            const ownerAccount = yield this.getOwnerAccount();
            if (!ownerAccount) {
                throw new Error('CPK ownerAccount uninitialized');
            }
            const safeExecTxParams = this.getSafeExecTxParams(transactions);
            const sendOptions = normalizeGasLimit(Object.assign(Object.assign({}, options), { from: ownerAccount }));
            const codeAtAddress = yield __classPrivateFieldGet(this, _ethLibAdapter).getCode(this.address);
            const isDeployed = codeAtAddress !== '0x';
            const txManager = !isDeployed ? new CpkTransactionManager() : __classPrivateFieldGet(this, _transactionManager);
            const cpkContracts = {
                safeContract: __classPrivateFieldGet(this, _contract),
                proxyFactory: __classPrivateFieldGet(this, _proxyFactory),
                masterCopyAddress: __classPrivateFieldGet(this, _masterCopyAddress),
                fallbackHandlerAddress: __classPrivateFieldGet(this, _fallbackHandlerAddress)
            };
            return txManager.execTransactions({
                ownerAccount,
                safeExecTxParams,
                transactions,
                contracts: cpkContracts,
                ethLibAdapter: __classPrivateFieldGet(this, _ethLibAdapter),
                saltNonce: __classPrivateFieldGet(this, _saltNonce),
                isDeployed,
                isConnectedToSafe: __classPrivateFieldGet(this, _isConnectedToSafe),
                sendOptions
            });
        });
    }
    getModules() {
        return __awaiter(this, void 0, void 0, function* () {
            const isProxyDeployed = yield this.isProxyDeployed();
            if (!isProxyDeployed) {
                throw new Error('CPK Proxy contract is not deployed');
            }
            if (!__classPrivateFieldGet(this, _contract)) {
                throw new Error('CPK contract uninitialized');
            }
            return yield __classPrivateFieldGet(this, _contract).call('getModules', []);
        });
    }
    isModuleEnabled(moduleAddress) {
        return __awaiter(this, void 0, void 0, function* () {
            const isProxyDeployed = yield this.isProxyDeployed();
            if (!isProxyDeployed) {
                throw new Error('CPK Proxy contract is not deployed');
            }
            if (!__classPrivateFieldGet(this, _contract)) {
                throw new Error('CPK contract uninitialized');
            }
            const modules = yield __classPrivateFieldGet(this, _contract).call('getModules', []);
            const selectedModules = modules.filter((module) => module.toLowerCase() === moduleAddress.toLowerCase());
            return selectedModules.length > 0;
        });
    }
    enableModule(moduleAddress) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!__classPrivateFieldGet(this, _contract)) {
                throw new Error('CPK contract uninitialized');
            }
            if (!this.address) {
                throw new Error('CPK address uninitialized');
            }
            return yield this.execTransactions([
                {
                    to: this.address,
                    data: yield __classPrivateFieldGet(this, _contract).encode('enableModule', [moduleAddress]),
                    operation: CPK.Call
                }
            ]);
        });
    }
    disableModule(moduleAddress) {
        return __awaiter(this, void 0, void 0, function* () {
            const isProxyDeployed = yield this.isProxyDeployed();
            if (!isProxyDeployed) {
                throw new Error('CPK Proxy contract is not deployed');
            }
            if (!__classPrivateFieldGet(this, _contract)) {
                throw new Error('CPK contract uninitialized');
            }
            if (!this.address) {
                throw new Error('CPK address uninitialized');
            }
            const modules = yield __classPrivateFieldGet(this, _contract).call('getModules', []);
            const index = modules.findIndex((module) => module.toLowerCase() === moduleAddress.toLowerCase());
            const prevModuleAddress = index === 0 ? sentinelModules : modules[index - 1];
            return yield this.execTransactions([
                {
                    to: this.address,
                    data: yield __classPrivateFieldGet(this, _contract).encode('disableModule', [prevModuleAddress, moduleAddress]),
                    operation: CPK.Call
                }
            ]);
        });
    }
    getSafeExecTxParams(transactions) {
        if (transactions.length === 1) {
            return standardizeTransaction(transactions[0]);
        }
        if (!__classPrivateFieldGet(this, _multiSend)) {
            throw new Error('CPK MultiSend uninitialized');
        }
        return {
            to: __classPrivateFieldGet(this, _multiSend).address,
            value: 0,
            data: this.encodeMultiSendCallData(transactions),
            operation: CPK.DelegateCall
        };
    }
}
_safeAppsSdkConnector = new WeakMap(), _ethLibAdapter = new WeakMap(), _transactionManager = new WeakMap(), _networks = new WeakMap(), _ownerAccount = new WeakMap(), _saltNonce = new WeakMap(), _isConnectedToSafe = new WeakMap(), _contract = new WeakMap(), _multiSend = new WeakMap(), _proxyFactory = new WeakMap(), _masterCopyAddress = new WeakMap(), _fallbackHandlerAddress = new WeakMap();
CPK.Call = OperationType.Call;
CPK.DelegateCall = OperationType.DelegateCall;
export default CPK;
//# sourceMappingURL=CPK.js.map